/**
 * Authentication Context with Supabase
 *
 * Manages user authentication state using Supabase Auth.
 * Fetches user profile from backend to get role and company info.
 */

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithPassword as supabaseSignIn,
  signOut as supabaseSignOut,
  getSession,
  onAuthStateChange,
  getAccessToken,
  isSupabaseConfigured,
} from './supabase';
import { apiClient } from './api';

/**
 * User role types.
 */
export type UserRole = 'super_admin' | 'company_admin' | 'trainer';

/**
 * User permissions.
 */
export interface UserPermissions {
  can_import_learners?: boolean;
  can_view_all_assessments?: boolean;
}

/**
 * Company info.
 */
export interface Company {
  id: string;
  name: string;
  slug: string;
}

/**
 * User profile from backend.
 */
export interface UserProfile {
  id: string;
  email: string;
  name?: string;
  company_id?: string;
  role: UserRole;
  permissions: UserPermissions;
  created_at: string;
  status: string;
}

/**
 * Authentication state.
 */
export interface AuthState {
  /** Authenticated user profile (null if not authenticated) */
  user: UserProfile | null;
  /** User's company (null if super admin or not loaded) */
  company: Company | null;
  /** Current role */
  role: UserRole | null;
  /** User permissions */
  permissions: UserPermissions;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is still loading */
  isLoading: boolean;
  /** Whether Supabase is configured */
  isConfigured: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Auth context value with actions.
 */
interface AuthContextValue extends AuthState {
  /** Sign in with email/password */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Refresh user profile */
  refreshProfile: () => Promise<void>;
  /** Get access token for API calls */
  getToken: () => Promise<string | null>;
  /** Legacy API: tenant ID for backwards compatibility */
  tenantId: string;
  /** Legacy API: actor ID for backwards compatibility */
  actorId: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * API base URL (uses proxy in dev).
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Fetches user profile from backend.
 */
async function fetchUserProfile(token: string): Promise<{ user: UserProfile; company: Company | null }> {
  const response = await fetch(`${API_BASE}/api/v1/users/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('User not found in system. Contact your administrator.');
    }
    throw new Error('Failed to fetch user profile');
  }

  const data = await response.json();
  return {
    user: data.user,
    company: data.company || null,
  };
}

/**
 * Auth provider component.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    company: null,
    role: null,
    permissions: {},
    isAuthenticated: false,
    isLoading: true,
    isConfigured: isSupabaseConfigured(),
    error: null,
  });

  /**
   * Load user profile from backend.
   */
  const loadProfile = useCallback(async (token: string) => {
    try {
      const { user, company } = await fetchUserProfile(token);
      setState((prev) => ({
        ...prev,
        user,
        company,
        role: user.role,
        permissions: user.permissions,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      console.error('Failed to load profile:', err);
      setState((prev) => ({
        ...prev,
        user: null,
        company: null,
        role: null,
        permissions: {},
        isAuthenticated: false,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load profile',
      }));
    }
  }, []);

  /**
   * Initialize auth state from existing session.
   */
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Supabase not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
      }));
      return;
    }

    // Check for existing session
    getSession().then((session) => {
      if (session?.access_token) {
        loadProfile(session.access_token);
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    });

    // Subscribe to auth changes
    const unsubscribe = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.access_token) {
        await loadProfile(session.access_token);
      } else if (event === 'SIGNED_OUT') {
        setState((prev) => ({
          ...prev,
          user: null,
          company: null,
          role: null,
          permissions: {},
          isAuthenticated: false,
          error: null,
        }));
      } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // Token refreshed, profile should still be valid
        // Optionally refresh profile here if needed
      }
    });

    return () => {
      unsubscribe();
    };
  }, [loadProfile]);

  // Bridge auth to API client
  useEffect(() => {
    if (state.isAuthenticated) {
      apiClient.setTokenProvider(() => getAccessToken());
    } else {
      apiClient.setTokenProvider(null);
    }
  }, [state.isAuthenticated]);

  /**
   * Sign in with email and password.
   */
  const signIn = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const { session } = await supabaseSignIn(email, password);
      if (session?.access_token) {
        await loadProfile(session.access_token);
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Sign in failed',
      }));
      throw err;
    }
  }, [loadProfile]);

  /**
   * Sign out.
   */
  const signOut = useCallback(async () => {
    try {
      await supabaseSignOut();
      setState((prev) => ({
        ...prev,
        user: null,
        company: null,
        role: null,
        permissions: {},
        isAuthenticated: false,
        error: null,
      }));
    } catch (err) {
      console.error('Sign out error:', err);
    }
  }, []);

  /**
   * Refresh user profile.
   */
  const refreshProfile = useCallback(async () => {
    const token = await getAccessToken();
    if (token) {
      await loadProfile(token);
    }
  }, [loadProfile]);

  /**
   * Get access token for API calls.
   */
  const getToken = useCallback(async () => {
    return getAccessToken();
  }, []);

  // Legacy API compatibility
  const tenantId = state.company?.id || '';
  const actorId = state.user?.id || '';

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    refreshProfile,
    getToken,
    tenantId,
    actorId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for legacy Access API compatibility.
 */
export function useAccess() {
  const auth = useAuth();

  return {
    isConfigured: auth.isAuthenticated,
    configureAccess: () => {
      console.warn('configureAccess is deprecated. Use signIn instead.');
    },
    clearAccess: auth.signOut,
    tenantId: auth.tenantId,
    actorId: auth.actorId,
  };
}

// Re-export for backward compatibility
export { AuthProvider as AccessProvider };

/**
 * Hook to check if user has a specific role.
 */
export function useHasRole(role: UserRole): boolean {
  const { role: userRole } = useAuth();
  if (!userRole) return false;

  const roleLevel: Record<UserRole, number> = {
    super_admin: 100,
    company_admin: 50,
    trainer: 10,
  };

  return roleLevel[userRole] >= roleLevel[role];
}

/**
 * Hook to check if user has a specific permission.
 */
export function useHasPermission(permission: keyof UserPermissions): boolean {
  const { role, permissions } = useAuth();

  // Super admins and company admins have all permissions
  if (role === 'super_admin' || role === 'company_admin') {
    return true;
  }

  return permissions[permission] === true;
}

/**
 * HOC to require authentication.
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function WithAuth(props: P) {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate('/');
      }
    }, [isLoading, isAuthenticated, navigate]);

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return null;
    }

    return <Component {...props} />;
  };
}
